import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import UnifiedFileUploader from "@enerex/unified-ui-react/FileUploader";

type AcceptConfig = string | string[] | Record<string, string[]>;

type UnifiedFileUploaderProps = {
  value?: File[];
  onChange?: (files: File[]) => void;
  accept?: AcceptConfig;
  disabled?: boolean;
  invalid?: boolean;
  multiple?: boolean;
  customIcon?: ReactNode;
  customPlaceholder?: ReactNode;
  customFileComponent?: (file: File, removeFile: () => void) => ReactNode;
  filesContainerClass?: string;
  onRejected?: (files: File[]) => void;
  [key: string]: unknown;
};

const getFileExtension = (fileName: string) => {
  const index = fileName.lastIndexOf(".");
  return index >= 0 ? fileName.slice(index).toLowerCase() : "";
};

const normalizeAccept = (accept?: AcceptConfig) => {
  if (!accept) {
    return [];
  }

  if (Array.isArray(accept)) {
    return accept.flatMap((entry) => entry.split(","));
  }

  if (typeof accept === "string") {
    return accept.split(",");
  }

  return Object.entries(accept).flatMap(([mimeType, extensions]) => [
    mimeType,
    ...extensions,
  ]);
};

const fileMatchesAccept = (file: File, acceptEntries: string[]) => {
  if (acceptEntries.length === 0) {
    return true;
  }

  const extension = getFileExtension(file.name);
  const type = file.type.toLowerCase();

  return acceptEntries.some((entry) => {
    const value = entry.trim().toLowerCase();

    if (!value) {
      return false;
    }

    if (value.startsWith(".")) {
      return extension === value;
    }

    if (value.endsWith("/*")) {
      return type.startsWith(value.slice(0, -1));
    }

    return type === value;
  });
};

const UnifiedFileUploaderWrapper = ({
  value,
  onChange,
  accept,
  disabled = false,
  invalid = false,
  onRejected,
  ...props
}: UnifiedFileUploaderProps) => {
  const [internalFiles, setInternalFiles] = useState<File[]>([]);
  const [hasRejectedFiles, setHasRejectedFiles] = useState(false);
  const acceptEntries = useMemo(() => normalizeAccept(accept), [accept]);
  const selectedFiles = value ?? internalFiles;

  const handleChange = (files: File[]) => {
    if (disabled) {
      return;
    }

    const acceptedFiles = files.filter((file) =>
      fileMatchesAccept(file, acceptEntries)
    );
    const rejectedFiles = files.filter(
      (file) => !fileMatchesAccept(file, acceptEntries)
    );

    setHasRejectedFiles(rejectedFiles.length > 0);

    if (rejectedFiles.length > 0) {
      onRejected?.(rejectedFiles);
    }

    if (onChange) {
      onChange(acceptedFiles);
      return;
    }

    setInternalFiles(acceptedFiles);
  };

  return (
    <div
      aria-disabled={disabled}
      style={{
        pointerEvents: disabled ? "none" : undefined,
        opacity: disabled ? 0.65 : undefined,
      }}
    >
      <UnifiedFileUploader
        {...props}
        value={selectedFiles}
        onChange={handleChange}
        invalid={invalid || hasRejectedFiles}
      />
    </div>
  );
};

export default UnifiedFileUploaderWrapper;
